import { AsyncPipe, CommonModule, CurrencyPipe, DatePipe, JsonPipe, NgIf } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IGX_GRID_DIRECTIVES,
  IgxActionStripModule,
  IgxExcelExporterOptions,
  IgxExcelExporterService,
  IgxGridComponent,
  IgxGridModule,
  IgxSwitchModule,
  IgxTabsModule,
  IPinningConfig,
  RowPinningPosition,
} from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import {
  IAgingCommentsDialogUpdate,
  IInvoiceAsOfAgingResult,
  IMiInvoiceDateReadOnlyStatusRequest,
  IUserListByLotNumberResponse,
} from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { environment } from 'src/environments/environment';
import { IgxPreventDocumentScrollModule } from 'src/app/shared/directives/prevent-scroll.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonToggle, MatButtonToggleModule } from '@angular/material/button-toggle';

@Component({
  selector: 'app-mi-due-date-readonly-status',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    IgxGridComponent,
    HeaderTitleLotDataComponent,
    MatTooltipModule,
    MatButtonModule,
    MatDialogModule,
    IgxActionStripModule,
    IgxSwitchModule,
    IgxPreventDocumentScrollModule,
    MatCheckboxModule,
    FormsModule,
    NgIf,
    MatButtonToggleModule,
    FormsModule,
  ],
  templateUrl: './mi-due-date-readonly-status.component.html',
  styleUrl: './mi-due-date-readonly-status.component.scss',
})
export class MiDueDateReadonlyStatusComponent implements OnInit {
  #_dataService = inject(DataService);
  #_destroyRefService = inject(DestroyRef);
  #_snackBar = inject(MatSnackBar);
  #_messageService = inject(MessageDialogService);
  _appGeneralState = inject(AppGeneralStateService);
  mIInvoiceDateReadOnlyStatus: boolean;
  readOnlySetting: string;

  constructor() {
    this.getMiInvoiceDateStatusForLot(this._appGeneralState.currentLotNumber);
  }

  ngOnInit(): void {
    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.getMiInvoiceDateStatusForLot(this._appGeneralState.currentLotNumber),
    });
  }

  getMiInvoiceDateStatusForLot(lotNumber: string) {
    this.#_dataService
      .getMiInvoiceDateReadOnlyStatus(lotNumber)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.mIInvoiceDateReadOnlyStatus = res;
        },
        error: (err: any) => {
          this.#_snackBar.dismiss();
          this.#_messageService.snackBarTopCenterTimed(
            'An unexpected error occurred while getting the readonly status for this lot.  Please try again.',
            5000,
          );
        },
        complete: () => {
          if (this.mIInvoiceDateReadOnlyStatus) {
            this.readOnlySetting = 'readonly';
          } else {
            this.readOnlySetting = 'notreadonly';
          }
        },
      });
  }

  onSaveMiReadOnlyState(): void {
    let miUpdateSettings: IMiInvoiceDateReadOnlyStatusRequest = {} as IMiInvoiceDateReadOnlyStatusRequest;

    miUpdateSettings.lotNumber = this._appGeneralState.currentLotNumber;
    miUpdateSettings.isReadOnly = this.readOnlySetting == 'readonly';

    this.#_dataService
      .setMiInvoiceDateReadOnlyStatus(miUpdateSettings)
      .pipe(takeUntilDestroyed(this.#_destroyRefService))
      .subscribe({
        next: (res) => {
          this.mIInvoiceDateReadOnlyStatus = this.readOnlySetting == 'readonly';
        },
        error: (err: any) => {
          this.#_snackBar.dismiss();
          this.#_messageService.snackBarTopCenterTimed(
            'An unexpected error occurred while setting the readonly status for this lot.  Please try again.',
            5000,
          );
        },
        complete: () => {
          this.#_messageService.snackBarTopCenterTimed(
            'The readonly/edit MI Invoice Date has been updated for this lot.',
            5000,
          );
        },
      });
  }
}
